/**
 * Timed construction + the shroom rush (client side).
 *
 * Two rules shape everything here:
 *   • The SERVER owns completion. `isReady` / `remainingMs` / `rushCost` come off
 *     the farm snapshot; the client only interpolates the countdown between polls
 *     and, when its interpolation hits zero, ASKS the server for a fresh snapshot
 *     instead of promoting the building itself. That is why {@link isUnderConstruction}
 *     reads the flag and never the clock.
 *   • Anything that costs shrooms goes through the action funnel and is followed by
 *     refreshBag + refreshHud — the hotbar and the wallet chips cache their numbers
 *     and a stale balance reads to the player as "my shrooms were not spent".
 */
import { el, toast } from '../../dom';
import { fieldApi, shopApi, type Building, type Plot } from '../../game-api';
import { ApiError } from '../../../core/network/api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { richText } from '../../ui/icons';
import { playSfx } from '../../audio/audio';
import { modalWrap } from '../../ui/panel';
import { BUILDINGS, cropById, liveRemaining, refreshBag, setFarm, type FarmCtx } from './context';

/**
 * Is this building still scaffolding?
 *
 * Deliberately the server flag alone. Older servers omit the field entirely, and
 * an absent flag must mean "built" — otherwise every existing farm would sprout
 * scaffolding after a deploy.
 */
export const isUnderConstruction = (b: Building): boolean => b.isReady === false;

/** Is this plot growing something the player could pay to ripen right now? */
export const canRushCrop = (p: Plot): boolean =>
  p.state === 'PLANTED' && !!p.cropId && !p.ready && (p.rushCost ?? 0) > 0;

/**
 * Countdown label. Everything rounds UP, at every unit: a bar that still has work
 * left must never show "0s" (the last tick reads "1s" and then the sprite swaps),
 * and — since the hours form drops the seconds entirely — a 1h00m01s build must
 * read "1h 01m" rather than the floor "1h 00m", which is a minute of work the
 * player was told he did not have to wait for. Placing a 40-minute build therefore
 * shows "40m 00s" on the very first frame even when the snapshot came back a few
 * hundred ms short of the full duration.
 */
export function fmtCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const s = total % 60;
  let h = Math.floor(total / 3600);
  const mFloor = Math.floor((total % 3600) / 60);
  if (h > 0) {
    // Seconds are not shown at this scale, so they round into the minute; 59m60s
    // carries into the next hour rather than printing "1h 60m".
    let m = mFloor + (s > 0 ? 1 : 0);
    if (m === 60) { h += 1; m = 0; }
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }
  if (mFloor > 0) return `${mFloor}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/**
 * Mirror of the server's price (building.math.rushCostShrooms): THREE shrooms per
 * remaining minute, floor of 3 — owner, 2026-08-17 ("a 60-minute building costs
 * 180 shrooms; at 30 minutes it costs half of that"). Was 1/min with a floor of 2,
 * which quoted a third of what the server now charges.
 *
 * Used for the LIVE quote while the panel is open (the wire number is priced at
 * the moment of the snapshot and only gets staler); the amount actually charged
 * is always the server's.
 */
export const RUSH_PER_MIN = 3;
export const rushPrice = (remainingMs: number): number =>
  Math.max(RUSH_PER_MIN, Math.ceil(remainingMs / 60_000) * RUSH_PER_MIN);

/**
 * The live quote for a building. The snapshot's `rushCost` was priced when the
 * snapshot was taken and only ever gets STALER (and higher) as the countdown runs,
 * while the server re-prices off the remaining time at the moment of the call — so
 * take the lower of the two. Quoting the stale number greys out purchases the
 * player can afford and states a price he will never be charged.
 */
export const buildRushCost = (b: Building, liveMs: number): number =>
  Math.min(b.rushCost ?? Number.POSITIVE_INFINITY, rushPrice(liveMs));

// ─── Completion refresh ──────────────────────────────────────────────────────
// When our interpolated countdown reaches zero we do NOT flip the sprite; we pull
// a fresh farm so the server decides. One request per completion, guarded so the
// 1 Hz repaint can't spam it while the response is in flight.
let refreshing = false;

/** Called from the repaint when a countdown has run out. Idempotent per flight. */
export function requestFinishRefresh(c: FarmCtx): void {
  if (refreshing || !c.alive) return;
  refreshing = true;
  void c.load()
    .then(() => { if (c.alive) void c.game.refreshHud(); })
    .catch(() => undefined)
    .finally(() => { refreshing = false; });
}

/** Test seam: forget the in-flight guard between cases. */
export function __resetConstruction(): void { refreshing = false; }

// ─── Rush panel ──────────────────────────────────────────────────────────────
let rushOverlay: HTMLElement | null = null;
/** The 1 Hz re-quote (see openRushPanel) and the last wallet answer it styles against. */
let rushTimer: number | null = null;
let lastBalance: number | null = null;
export function closeRushPanel(): void {
  rushOverlay?.remove();
  rushOverlay = null;
  if (rushTimer !== null) { clearInterval(rushTimer); rushTimer = null; }
  lastBalance = null;
  if (rushEsc) { window.removeEventListener('keydown', rushEsc, true); rushEsc = null; }
}
// Escape closes it, like every other panel: in immersive mode the page chrome a
// popup is normally dismissed against is hidden, so the keyboard has to work.
let rushEsc: ((e: KeyboardEvent) => void) | null = null;

interface RushTarget {
  what: 'build' | 'crop';
  tx: number;
  ty: number;
  title: string;       // "Farmhouse" / "Wheat"
  remainingMs: number;
  cost: number;
}

/**
 * The "Finish now / Ripen now" popup. Shows the quote, the player's shroom balance
 * and the time it buys back. Confirming runs the matching rush endpoint through
 * the funnel and refreshes everything the purchase touched.
 */
export function openRushPanel(c: FarmCtx, t: RushTarget): void {
  closeRushPanel();
  playSfx('ui_open');
  const verb = t.what === 'build' ? 'Finish now' : 'Ripen now';
  const balance = el('span', 'text-shroom font-mono', {}, '…');
  const confirm = el('button', 'w-full px-3 py-2 mb-2 rounded bg-shroom/20 border border-shroom text-shroom hover:bg-shroom/30',
    { onClick: () => void doRush(c, t) },
    el('span', 'inline-flex items-center justify-center gap-1', {}, ...richText(`${verb} · 🍄 ${t.cost}`)));
  const timeLine = el('p', 'text-sm text-text-dim mb-1', {},
    t.what === 'build' ? `Under construction — ${fmtCountdown(t.remainingMs)} left.` : `Growing — ${fmtCountdown(t.remainingMs)} left.`);

  const card = el('div', 'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[90vw] shadow-lift text-center sheet-card game-font', {},
    el('h3', 'font-heading text-gold mb-1', {}, t.title),
    timeLine,
    el('p', 'text-[11px] text-text-dim mb-4 inline-flex items-center justify-center gap-1', {},
      ...richText('Your Shrooms: '), balance),
    confirm,
    el('button', 'w-full px-3 py-2 rounded border border-border text-text-dim hover:text-text',
      { onClick: () => closeRushPanel() }, 'Wait it out'));

  // modalWrap, not a hand-rolled overlay. This was `fixed inset-0` with no
  // background, so the booster panel floated over a fully lit farm with nothing
  // behind it — no dim, no separation from the world, and it started at y=0, under
  // the header chrome. Every other dialog in the game goes through this helper,
  // which dims the backdrop, starts below whatever chrome is showing, and applies
  // the compact-phone treatment.
  /**
   * THE PRICE TICKS DOWN WHILE THE PANEL IS OPEN.
   *
   * It used to be rendered once from `t.cost` and then sat there: the countdown
   * was frozen too, so a panel left open for ten minutes quoted ten-minute-old
   * numbers, and at the x3 rate that is a 30-shroom lie. Now both the remaining
   * time and the quote are recomputed each second from the SAME clock the panel
   * was opened against, and `t.cost` is updated with them so the confirm handler
   * and the "not enough Shrooms" message use the live figure rather than the one
   * the panel opened with.
   */
  const openedAt = Date.now();
  const openedRemaining = t.remainingMs;
  const tick = () => {
    const left = Math.max(0, openedRemaining - (Date.now() - openedAt));
    t.remainingMs = left;
    t.cost = rushPrice(left);
    timeLine.textContent = t.what === 'build'
      ? `Under construction — ${fmtCountdown(left)} left.`
      : `Growing — ${fmtCountdown(left)} left.`;
    confirm.replaceChildren(el('span', 'inline-flex items-center justify-center gap-1', {}, ...richText(`${verb} · 🍄 ${t.cost}`)));
    // Balance was already fetched; re-apply the affordability styling against the
    // NEW price, or a panel that started unaffordable stays greyed out after the
    // price has fallen below what the player holds.
    if (lastBalance !== null) {
      balance.className = lastBalance < t.cost ? 'text-error font-mono' : 'text-shroom font-mono';
      confirm.classList.toggle('opacity-60', lastBalance < t.cost);
    }
  };
  rushTimer = window.setInterval(tick, 1000);

  rushOverlay = modalWrap(() => closeRushPanel(), card, 'z-[70]');
  document.body.appendChild(rushOverlay);
  rushEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); closeRushPanel(); } };
  window.addEventListener('keydown', rushEsc, true);

  // Balance is informational, so it loads after the panel is already on screen —
  // and short funds are still reported by the server on confirm, never guessed here.
  void shopApi.wallet()
    .then((w) => {
      lastBalance = w.shrooms;
      balance.textContent = w.shrooms.toLocaleString();
      if (w.shrooms < t.cost) {
        balance.className = 'text-error font-mono';
        confirm.classList.add('opacity-60');
      }
    })
    .catch(() => { balance.textContent = '?'; });
}

async function doRush(c: FarmCtx, t: RushTarget): Promise<void> {
  const type = t.what === 'build' ? 'field.rush-build' : 'field.rush-crop';
  try {
    await dispatchAction(type, async (actionId) => {
      // NOTE: unlike build()/unbuild(), the rush endpoints answer with a WRAPPER
      // ({ spent, farm }) — assigning the response straight to c.farm would wipe
      // the farm state.
      const res = t.what === 'build'
        ? await fieldApi.rushBuild(t.tx, t.ty, actionId)
        : await fieldApi.rushCrop(t.tx, t.ty, actionId);
      closeRushPanel();
      // The panel can outlive its view (left the farm with it open): touching a
      // destroyed ctx would repaint against a torn-down Pixi tree.
      if (!c.alive) return;
      setFarm(c, res.farm);
      playSfx('build_place');
      toast(t.what === 'build' ? `${t.title} finished · 🍄 ${res.spent}` : `${t.title} ripened · 🍄 ${res.spent}`);
      // Shrooms left the wallet and the snapshot changed: both caches must be told.
      await c.game.refreshHud();
      refreshBag(c);
      // Completion can change what is walkable — a rushed bridge/road deck only
      // clears its (blocked) tile once the grid is rebuilt against `isReady: true`.
      // Skipping this made the rush a paid no-op: finished art, impassable tile.
      if (t.what === 'build') rebuildCollision?.(c);
      repaint?.(c);
    }, { key: `${t.tx},${t.ty}` });
  } catch (e) {
    if (isDropped(e)) return;
    const msg = e instanceof ApiError ? e.message : 'Could not finish that';
    // The one error worth rewriting: "Insufficient balance" says nothing about
    // what to do next.
    toast(/insufficient/i.test(msg) ? `Not enough Shrooms — you need 🍄 ${t.cost}` : msg, 'error');
  }
}

// plots.ts owns the repaint and imports this module, so it registers its painter
// here rather than us importing it back (that cycle breaks the pixi module init).
let repaint: ((c: FarmCtx) => void) | null = null;
// Same reason for scene.buildCollision: importing it here would close the
// construction → scene → plots → construction cycle and break pixi's module init.
let rebuildCollision: ((c: FarmCtx) => void) | null = null;
export function setConstructionRepainter(fn: (c: FarmCtx) => void, collide?: (c: FarmCtx) => void): void {
  repaint = fn;
  if (collide) rebuildCollision = collide;
}

/** Open the rush panel for a building under construction. */
export function openBuildRush(c: FarmCtx, b: Building): void {
  const live = liveRemaining(c, b.remainingMs ?? 0);
  openRushPanel(c, {
    what: 'build', tx: b.tx, ty: b.ty,
    title: BUILDINGS[b.kind]?.name ?? b.kind,
    remainingMs: live,
    cost: buildRushCost(b, live),
  });
}

/** Open the rush panel for a growing crop. */
export function openCropRush(c: FarmCtx, p: Plot): void {
  const live = liveRemaining(c, p.remainingMs);
  openRushPanel(c, {
    what: 'crop', tx: p.tx, ty: p.ty,
    title: cropById(c, p.cropId!)?.name ?? 'Crop',
    remainingMs: live,
    cost: Math.min(p.rushCost ?? Number.POSITIVE_INFINITY, rushPrice(live)),
  });
}
